if ( typeof BundleTogether !== 'function' ) {

  class BundleTogether extends HTMLElement {

    constructor() {
      super();

      this.items = Array.from(this.querySelectorAll('[data-bundle-item]'));
      this.totalElement = this.querySelector('[data-bundle-total]');
      this.addButton = this.querySelector('[data-bundle-add]');
      this.statusElement = this.querySelector('[data-bundle-status]');

      this.items.forEach(item => {
        const checkbox = item.querySelector('[data-bundle-check]');
        const variant = item.querySelector('[data-bundle-variant]');

        if ( checkbox ) {
          checkbox.addEventListener('change', () => this.update());
        }
        if ( variant && variant.tagName === 'SELECT' ) {
          variant.addEventListener('change', () => {
            this._syncItemPrice(item);
            this.update();
          });
        }
      });

      if ( this.addButton ) {
        this.addButton.addEventListener('click', () => this.addToCart());
      }

      this.update();
    }

    // --- state ---------------------------------------------------------

    _selectedVariant(item) {
      const variant = item.querySelector('[data-bundle-variant]');
      if ( ! variant ) return null;

      const source = variant.tagName === 'SELECT'
        ? variant.options[variant.selectedIndex]
        : variant;

      if ( ! source ) return null;

      return {
        id: variant.tagName === 'SELECT' ? source.value : variant.value,
        price: Number(source.getAttribute('data-price') || 0),
        compareAtPrice: Number(source.getAttribute('data-compare-at-price') || 0),
        available: source.getAttribute('data-available') !== 'false'
      };
    }

    _syncItemPrice(item) {
      const selected = this._selectedVariant(item);
      if ( ! selected ) return;

      const priceElement = item.querySelector('[data-bundle-item-price]');
      const compareElement = item.querySelector('[data-bundle-item-compare]');

      if ( priceElement ) {
        priceElement.innerHTML = this._formatMoney(selected.price);
      }
      if ( compareElement ) {
        if ( selected.compareAtPrice > selected.price ) {
          compareElement.innerHTML = this._formatMoney(selected.compareAtPrice);
          compareElement.hidden = false;
        } else {
          compareElement.hidden = true;
        }
      }

      const checkbox = item.querySelector('[data-bundle-check]');
      if ( checkbox ) {
        checkbox.disabled = ! selected.available;
        if ( ! selected.available ) checkbox.checked = false;
      }
      item.classList.toggle('bundle-together__item--unavailable', ! selected.available);
    }

    _selectedItems() {
      return this.items.reduce((collected, item) => {
        const checkbox = item.querySelector('[data-bundle-check]');
        if ( ! checkbox || ! checkbox.checked || checkbox.disabled ) return collected;

        const selected = this._selectedVariant(item);
        if ( ! selected || ! selected.available || ! selected.id ) return collected;

        collected.push({
          id: selected.id,
          price: selected.price,
          productId: item.getAttribute('data-product-id') || ''
        });
        return collected;
      }, []);
    }

    update() {
      const selected = this._selectedItems();
      const total = selected.reduce((sum, item) => sum + item.price, 0);

      if ( this.totalElement ) {
        this.totalElement.innerHTML = this._formatMoney(total);
      }
      if ( this.addButton ) {
        this.addButton.disabled = selected.length === 0;
      }
    }

    _setStatus(message, isError) {
      if ( ! this.statusElement ) return;
      this.statusElement.textContent = message || '';
      this.statusElement.hidden = ! message;
      this.statusElement.classList.toggle('bundle-together__status--error', !!isError);
    }

    // --- cart ----------------------------------------------------------

    addToCart() {
      const selected = this._selectedItems();
      if ( ! selected.length ) return;

      const root = ( window.Shopify && Shopify.routes && Shopify.routes.root ) ? Shopify.routes.root : '/';
      const bundleTitle = this.dataset.bundleTitle || '';

      this._setStatus('');
      this.addButton.classList.add('working');
      this.addButton.disabled = true;

      fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          items: selected.map(item => ({
            id: item.id,
            quantity: 1,
            properties: bundleTitle ? { _bundle: bundleTitle } : {}
          }))
        })
      })
        .then(response => response.json().then(payload => ({ ok: response.ok, payload })))
        .then(({ ok, payload }) => {
          if ( ! ok ) {
            this._setStatus(payload.description || payload.message || '', true);
            return;
          }

          if ( typeof window.refreshCart === 'function' ) {
            window.refreshCart();
          }

          if ( window.KROWN && KROWN.functions && KROWN.functions.eventDispatcher ) {
            KROWN.functions.eventDispatcher('krown:cart:add', { bundle: bundleTitle });
          }
        })
        .catch(error => {
          console.log(error);
          this._setStatus(( window.KROWN && KROWN.settings.locales.cart_general_error ) || '', true);
        })
        .finally(() => {
          this.addButton.classList.remove('working');
          this.update();
        });
    }

    // --- helpers -------------------------------------------------------

    _formatMoney(cents) {
      const format = ( window.KROWN && KROWN.settings && KROWN.settings.shop_money_format ) || '{{amount}}';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholderRegex);
      if ( ! match ) return format;

      function formatWithDelimiters(number, precision, thousands = ',', decimal = '.') {
        if ( isNaN(number) || number === null ) return '0';
        const fixed = (number / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        return whole + ( parts[1] ? decimal + parts[1] : '' );
      }

      let value = '';
      switch ( match[1] ) {
        case 'amount':
          value = formatWithDelimiters(cents, 2); break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0); break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ','); break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' '); break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'"); break;
        default:
          value = formatWithDelimiters(cents, 2);
      }

      return format.replace(placeholderRegex, value);
    }

  }

  if ( typeof customElements.get('bundle-together') == 'undefined' ) {
    customElements.define('bundle-together', BundleTogether);
  }

}
